import React, { useRef, useState, useMemo } from 'react';
import { Trash2, Monitor, Smartphone, UploadCloud, ChevronDown, ChevronUp, AlignLeft, AlignCenter, AlignRight, Code2, Type, MousePointerClick, Palette } from 'lucide-react';

/* ──────────────────────────────────────────────────────────
   Lightweight HTML sanitiser (strips <script>, on*, javascript:)
   Used to safely render user-supplied custom HTML in preview.
   ────────────────────────────────────────────────────────── */
const sanitizeHTML = (html) => {
  if (!html) return '';
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/gi, '')
    .replace(/javascript\s*:/gi, '');
};

export default function BrandingEmailTab({ form, update, onUpload }) {
  // ── Derived form values ──────────────────────────────────
  const emailFooter   = form.email_footer || '© {{year}} Acme Corp. All rights reserved.\n123 Business Street, Suite 100\nSan Francisco, CA 94107';
  const emailLogo     = form.email_logo_url || form.logo_light_url || '';
  const emailTemplate = form.email_template || 'default';
  const themeColor    = form.theme_color || '#3B82F6';

  // Customisation values (stored under tenant_branding)
  const emailTitle       = form.email_title        || '';
  const emailButtonText  = form.email_button_text   || '';
  const emailButtonAlign = form.email_button_align  || 'left';
  const emailButtonColor = form.email_button_color  || themeColor;
  const emailCustomHtml  = form.email_custom_html   || '';

  // ── Local UI state ───────────────────────────────────────
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop | mobile
  const [showCustomise, setShowCustomise] = useState(false);
  const fileInputRef = useRef(null);

  // ── Logo upload handler ──────────────────────────────────
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ['image/png', 'image/svg+xml', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert('Invalid file format. Please upload PNG, SVG, JPG, or WebP.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('File too large. Maximum size allowed is 2MB.');
      return;
    }

    update('email_logo_url', URL.createObjectURL(file));
    if (onUpload) await onUpload('email_logo_url', file, 'light');
  };

  // ── Template catalogue ───────────────────────────────────
  const templates = [
    { id: 'default',      name: 'Default',      colors: ['bg-blue-500',   'bg-gray-200']   },
    { id: 'minimal',      name: 'Minimal',      colors: ['bg-gray-800',   'bg-gray-100']   },
    { id: 'notification', name: 'Notification',  colors: ['bg-green-500',  'bg-green-100']  },
    { id: 'alert',        name: 'Alert',         colors: ['bg-red-500',    'bg-red-100']    },
    { id: 'marketing',    name: 'Marketing',     colors: ['bg-purple-500', 'bg-purple-100'] },
  ];

  // ── Resolved preview values ──────────────────────────────
  const resolvedTitle      = emailTitle      || 'Hello {{user_name}}';
  const resolvedButtonText = emailButtonText || 'Verify Email Address';
  const resolvedBtnColor   = emailButtonColor || themeColor;
  const alignClass = { left: 'text-left', center: 'text-center', right: 'text-right' }[emailButtonAlign] || 'text-left';

  const commonButton = (overrideColor) => (
    <div className={alignClass}>
      <button
        className="px-6 py-2.5 rounded-lg text-white text-sm font-medium transition-opacity hover:opacity-90 inline-block mb-6"
        style={{ backgroundColor: overrideColor || resolvedBtnColor }}
      >
        {resolvedButtonText}
      </button>
    </div>
  );

  // ── Sanitised custom HTML (memoised) ─────────────────────
  const safeCustomHtml = useMemo(() => sanitizeHTML(emailCustomHtml), [emailCustomHtml]);

  // ── Template preview renderer ────────────────────────────
  const renderTemplatePreview = () => {
    const customBlock = safeCustomHtml
      ? <div className="mb-4 text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: safeCustomHtml }} />
      : null;

    switch (emailTemplate) {
      case 'minimal':
        return (
          <div className="w-full mb-6 text-center border-y border-gray-200 py-8">
            <h2 className="text-lg font-medium text-gray-900 mb-2">{resolvedTitle}</h2>
            <p className="text-sm text-gray-500 mb-6">We're excited to have you on board. Click the button below to verify your email address.</p>
            {customBlock}
            {commonButton()}
          </div>
        );
      case 'notification':
        return (
          <div className="bg-white rounded-xl overflow-hidden shadow-sm w-full mb-6 text-left border border-gray-100">
            <div className="bg-green-500 px-6 py-3">
              <span className="text-white text-sm font-semibold tracking-wide uppercase">New Notification</span>
            </div>
            <div className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-2">{resolvedTitle},</h2>
              <p className="text-sm text-gray-600 mb-6">We're excited to have you on board. Verify your email address to get started.</p>
              {customBlock}
              {commonButton()}
            </div>
          </div>
        );
      case 'alert':
        return (
          <div className="bg-white rounded-xl shadow-sm w-full mb-6 text-center border border-red-200 overflow-hidden">
            <div className="bg-red-50 p-4 border-b border-red-100 text-red-600 font-semibold flex items-center justify-center gap-2">
              Action Required
            </div>
            <div className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-2">{resolvedTitle}</h2>
              <p className="text-sm text-gray-600 mb-6">Please verify your email address immediately to secure your account.</p>
              {customBlock}
              {commonButton('#dc2626')}
            </div>
          </div>
        );
      case 'marketing':
        return (
          <div className="bg-white rounded-2xl shadow-lg w-full mb-6 text-center border-t-[12px]" style={{ borderColor: themeColor }}>
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{resolvedTitle} 🎉</h2>
              <p className="text-sm text-gray-600 leading-relaxed mb-6">
                We're thrilled to have you here. Verify your email to unlock all features and get started.
              </p>
              {customBlock}
              {commonButton()}
            </div>
          </div>
        );
      case 'default':
      default:
        return (
          <div className="bg-white rounded-xl p-8 shadow-sm w-full mb-6 text-left">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{resolvedTitle},</h2>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              We're excited to have you on board. Click the button below to verify your email address and get started.
            </p>
            {customBlock}
            {commonButton()}
            <p className="text-sm text-gray-500 mt-2 leading-relaxed">
              If you didn't create an account, you can safely ignore this email.
            </p>
            <hr className="my-6 border-gray-100" />
            <p className="text-sm font-medium text-gray-900 mb-1">Need help?</p>
            <p className="text-sm text-gray-600">Contact our support team for assistance.</p>
          </div>
        );
    }
  };

  // ── Alignment selector helper ────────────────────────────
  const AlignButton = ({ value, icon: Icon, label }) => (
    <button
      type="button"
      onClick={() => update('email_button_align', value)}
      title={label}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
        emailButtonAlign === value
          ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
          : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 hover:text-gray-700'
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );

  // ═══════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* ─── Left Column: Settings ──────────────────────── */}
        <div className="space-y-6">

          {/* Header */}
          <div>
            <h3 className="font-bold text-gray-900 text-lg">Email Settings</h3>
            <p className="text-sm text-gray-500 mt-1">Customize how emails look when sent from your platform.</p>
          </div>

          {/* Sender Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Sender Name
            </label>
            <input
              type="text"
              value={form.email_sender_name || ''}
              onChange={(e) => update('email_sender_name', e.target.value)}
              placeholder="e.g. Aidas Support"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none"
            />
            <p className="text-xs text-gray-500 mt-1">This name will appear as the sender in recipient inboxes.</p>
          </div>

          {/* Email Logo + Footer Template (side by side) */}
          <div className="grid grid-cols-2 gap-4">
            {/* Logo */}
            <div>
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                Email Logo <span className="text-gray-400 cursor-help rounded-full border border-gray-300 w-3.5 h-3.5 flex items-center justify-center text-[9px]">i</span>
              </label>
              <div
                className="border border-gray-200 rounded-lg p-3 bg-white flex items-center gap-3 relative h-[88px] group cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input type="file" className="hidden" ref={fileInputRef} onChange={handleLogoUpload} accept="image/png, image/svg+xml, image/jpeg, image/webp" />
                {emailLogo ? (
                  <>
                    <div className="flex-1 h-full flex items-center justify-center overflow-hidden">
                      <img src={emailLogo} alt="Email Logo" className="max-h-12 max-w-full object-contain" />
                    </div>
                    <button
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      onClick={(e) => { e.stopPropagation(); update('email_logo_url', null); }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <div className="flex-1 h-full flex flex-col items-center justify-center text-gray-400">
                    <UploadCloud className="w-5 h-5 mb-1" />
                    <span className="text-xs">Upload Logo</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">PNG, SVG, JPG, or WebP. Max size 2MB.</p>
            </div>

            {/* Footer Template */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Footer Template</h4>
              <textarea
                value={emailFooter}
                onChange={(e) => update('email_footer', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none h-[88px] resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">This footer will appear at the bottom of all emails.</p>
            </div>
          </div>

          {/* Email Template Selector */}
          <div className="border border-gray-100 rounded-xl bg-white p-6 shadow-sm">
            <h4 className="font-semibold text-gray-900 mb-1">Email Template</h4>
            <p className="text-sm text-gray-500 mb-4">Select a pre-built email template. You can use variables in the content.</p>

            <div className="flex flex-wrap gap-4">
              {templates.map(tpl => (
                <div
                  key={tpl.id}
                  onClick={() => update('email_template', tpl.id)}
                  className="flex flex-col items-center gap-2 cursor-pointer group"
                >
                  <div className={`relative w-[88px] h-[64px] rounded-lg border-2 flex flex-col p-1.5 gap-1 transition-all ${
                    emailTemplate === tpl.id
                      ? 'border-blue-500 bg-blue-50/30 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}>
                    {emailTemplate === tpl.id && (
                      <div className="absolute -mt-3 -ml-3 w-5 h-5 bg-blue-500 rounded-full border-2 border-white flex items-center justify-center">
                        <div className="w-1.5 h-1.5 bg-white rounded-full" />
                      </div>
                    )}
                    <div className={`w-1/2 h-1.5 ${tpl.colors[0]} rounded-sm mx-auto mb-1`} />
                    <div className={`w-3/4 h-1 ${tpl.colors[1]} rounded-sm`} />
                    <div className={`w-full h-1 ${tpl.colors[1]} rounded-sm`} />
                    <div className={`w-5/6 h-1 ${tpl.colors[1]} rounded-sm`} />
                    <div className={`w-1/3 h-2 ${tpl.colors[0]} rounded-sm mt-auto mx-auto`} />
                  </div>
                  <span className={`text-xs font-medium ${emailTemplate === tpl.id ? 'text-blue-600' : 'text-gray-600'}`}>{tpl.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Customize Template (collapsible) ──────────── */}
          <div className="border border-gray-100 rounded-xl bg-white shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setShowCustomise(!showCustomise)}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Code2 className="w-4 h-4 text-blue-600" />
                <span className="font-semibold text-gray-900 text-sm">Customize Template</span>
              </div>
              {showCustomise ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {showCustomise && (
              <div className="px-6 pb-6 space-y-5 border-t border-gray-100 pt-5">

                {/* Email Title */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                    <Type className="w-3.5 h-3.5 text-gray-400" /> Email Title
                  </label>
                  <input
                    type="text"
                    value={emailTitle}
                    onChange={(e) => update('email_title', e.target.value)}
                    placeholder="Hello {{user_name}}"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">Use {'{{user_name}}'} for dynamic names. Leave blank for default.</p>
                </div>

                {/* Button Text */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                    <MousePointerClick className="w-3.5 h-3.5 text-gray-400" /> Button Text
                  </label>
                  <input
                    type="text"
                    value={emailButtonText}
                    onChange={(e) => update('email_button_text', e.target.value)}
                    placeholder="Verify Email Address"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>

                {/* Button Position */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                    Button Position
                  </label>
                  <div className="flex gap-2">
                    <AlignButton value="left"   icon={AlignLeft}   label="Left" />
                    <AlignButton value="center" icon={AlignCenter} label="Center" />
                    <AlignButton value="right"  icon={AlignRight}  label="Right" />
                  </div>
                </div>

                {/* Button Color */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                    <Palette className="w-3.5 h-3.5 text-gray-400" /> Button Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={emailButtonColor}
                      onChange={(e) => update('email_button_color', e.target.value)}
                      className="w-9 h-9 rounded-lg border border-gray-200 cursor-pointer p-0.5"
                    />
                    <input
                      type="text"
                      value={emailButtonColor}
                      onChange={(e) => update('email_button_color', e.target.value)}
                      className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                      maxLength={7}
                    />
                    <button
                      type="button"
                      onClick={() => update('email_button_color', themeColor)}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline"
                    >
                      Reset to theme
                    </button>
                  </div>
                </div>

                {/* Custom HTML Block */}
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                    <Code2 className="w-3.5 h-3.5 text-gray-400" /> Custom HTML Block
                  </label>
                  <textarea
                    value={emailCustomHtml}
                    onChange={(e) => update('email_custom_html', e.target.value)}
                    placeholder={'<p style="color:#64748b;">Custom content here…</p>'}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none h-24 resize-y font-mono"
                  />
                  <p className="text-xs text-gray-400 mt-1">Raw HTML rendered below the template body. Script tags are stripped for security.</p>
                </div>
              </div>
            )}
          </div>

        </div>

        {/* ─── Right Column: Preview ──────────────────────── */}
        <div className="border border-gray-100 rounded-xl bg-[#f8fafc] p-6 flex flex-col h-full min-h-[500px]">

          {/* Header + device toggle */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h4 className="font-semibold text-gray-900">Email Preview</h4>
              <p className="text-sm text-gray-500">This is how your email will appear to recipients.</p>
            </div>
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-0.5 shadow-sm">
              <button
                type="button"
                onClick={() => setPreviewMode('desktop')}
                className={`p-1.5 rounded-md transition-colors ${
                  previewMode === 'desktop' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:text-gray-600'
                }`}
                title="Desktop preview"
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setPreviewMode('mobile')}
                className={`p-1.5 rounded-md transition-colors ${
                  previewMode === 'mobile' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:text-gray-600'
                }`}
                title="Mobile preview"
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Preview area */}
          <div className="flex-1 bg-gray-100 rounded-lg p-4 flex justify-center shadow-inner overflow-y-auto">
            <div
              className={`transition-all duration-300 ease-in-out flex flex-col items-center ${
                previewMode === 'mobile'
                  ? 'w-[375px] max-w-[375px] bg-white rounded-[2rem] border-[6px] border-gray-300 shadow-xl p-4 my-2'
                  : 'w-full max-w-full'
              }`}
            >
              {/* Logo */}
              {emailLogo && (
                <div className="h-10 mb-6 mt-4 w-full flex justify-center">
                  <img src={emailLogo} alt="Logo Preview" className="max-h-full max-w-full object-contain" />
                </div>
              )}

              {/* Template body */}
              <div className={`w-full ${previewMode === 'mobile' ? 'max-w-full px-0' : 'max-w-sm'}`}>
                {renderTemplatePreview()}
              </div>

              {/* Footer */}
              <div className="text-xs text-gray-400 text-center whitespace-pre-line leading-relaxed max-w-xs pb-4">
                {emailFooter}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
